import React, { useState, useEffect } from 'react';
import LocationList from '../../LocationList';
import { useTp } from '../../../context/TpContext';
import ModeHelpTooltip from './components/ModeHelpTooltip';
import SolutionDisplay from './components/SolutionDisplay';
import { useTransportSolver } from './hooks/useTransportSolver';

export default function AutoMode() {
  const { 
    locations, setLocations, setPath,
    autoRoutes, saveAutoRoutes, getStoredRoutes,
    handleUpdateVisiblePath
  } = useTp();
  
  const [showHelp, setShowHelp] = useState(false);
  const [tpConfig, setTpConfig] = useState({
    supply: [],
    demand: [],
    method: "vam"
  });
  
  const [restoredSolution, setRestoredSolution] = useState(null);
  const [restoredIsolationInfo, setRestoredIsolationInfo] = useState(null);
  
  const { loading, error, solution, isolationInfo, solveProblem } = useTransportSolver(locations, setPath, saveAutoRoutes);

  useEffect(() => {
    handleUpdateVisiblePath(null);
  }, []);
  
  useEffect(() => {
    const storedRoutes = getStoredRoutes('Auto Path');
    if (storedRoutes && storedRoutes.solution) {
      const locationIds = locations.map(loc => loc.id).sort();
      const storedLocationIds = storedRoutes.locations.map(loc => loc.id).sort();
      
      if (JSON.stringify(locationIds) === JSON.stringify(storedLocationIds)) {
        setRestoredSolution(storedRoutes.solution);
        setRestoredIsolationInfo(storedRoutes.isolationInfo);
        
        if (storedRoutes.mapData && storedRoutes.mapData.features.length > 0) {
          setPath(storedRoutes.mapData);
        }
        
        if (storedRoutes.method) {
          setTpConfig(prev => ({ ...prev, method: storedRoutes.method }));
        }
      } else {
        setRestoredSolution(null);
        setRestoredIsolationInfo(null);
      }
    }
  }, [locations, getStoredRoutes, setPath]);
  
  useEffect(() => {
    const supplies = locations.filter(loc => (loc.tpType || '').toLowerCase() === 'supply');
    const demands = locations.filter(loc => (loc.tpType || '').toLowerCase() === 'demand');
    
    setTpConfig(prev => ({
      ...prev,
      supply: supplies.map(loc => Number(loc.quantity || 0)),
      demand: demands.map(loc => Number(loc.quantity || 0))
    }));
  }, [locations]);

  const handleSolveTransport = () => {
    solveProblem(tpConfig.method);
  };
  
  const handleMethodChange = (event) => {
    const newMethod = event.target.value;
    setTpConfig(prev => ({ ...prev, method: newMethod }));
  };

  return (
    <div className="mode-content">
      <div className="mode-description">
        <div className="mode-title-container">
          <h3>Auto Mode</h3>
          <button className="mode-help-button" onClick={() => setShowHelp(true)} title="Help">?</button>
        </div>
        <p>
          Automatically solve transportation problems using distance calculations.
          Add supply and demand locations with quantities.
        </p>
      </div>
      
      {showHelp && <ModeHelpTooltip onClose={() => setShowHelp(false)} />}

      <div className="controls-section">
        <h4>Algorithm Configuration</h4>
        <div className="form-group">
          <label htmlFor="method-select">Solving Method:</label>
          <select
            id="method-select"
            value={tpConfig.method}
            onChange={handleMethodChange}
            className="form-control"
          >
            <option value="vam">Vogel's Approximation Method (VAM)</option>
            <option value="vam+modi">VAM + MODI Optimization</option>
            <option value="nw">Northwest Corner Method</option>
            <option value="lc">Least Cost Method</option>
            <option value="lp">Linear Programming (Exact)</option>
          </select>
        </div>

        <div className="solve-button-container">
          <button
            onClick={handleSolveTransport}
            disabled={loading || locations.length < 2}
            className="solve-button"
          >
            {loading ? 'Solving...' : 'Solve Transport Problem'}
          </button>
        </div>
      </div>

      <LocationList 
        mode="Auto Mode" 
        readOnly={true}
        showActions={true}
        allowDelete={false}
      />

      {error && (
        <div className="error-message">
          <h4>Error:</h4>
          <p>{error}</p>
        </div>
      )}

      <SolutionDisplay 
        solution={solution || restoredSolution}
        isolationInfo={isolationInfo || restoredIsolationInfo}
        locations={locations}
        setLocations={setLocations}
      />
    </div>
  );
}